import config from "core/config";
import { logger } from "library/core/utility";
import { AiChatRequest, AiChatResponse } from "./types";

const logPrefix = "[AiChatService]:";

class AiChatService {
  private get baseUrl(): string {
    if (process.env.NODE_ENV === "development") {
      return "/api/ai-chat";
    }
    return config.aiChatLambdaUrl;
  }

  private log(message: string, ...args: any[]) {
    logger.log("info", `${logPrefix} ${message}`, ...args);
  }

  private logError(message: string, ...args: any[]) {
    logger.log("error", `${logPrefix} ${message}`, ...args);
  }

  generateReply = async (request: AiChatRequest): Promise<AiChatResponse> => {
    this.log("generateReply started", {
      session_id: request.session_id,
      model_name: request.model_name,
    });

    const response = await fetch(this.baseUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request),
    });

    if (response.status === 502) {
      const errorText = await response.text();
      this.logError("generateReply 502", { errorText });
      throw new Error("AI service unavailable, please try again later");
    }

    const responseText = await response.text();

    if (!response.ok) {
      let errorMessage = "AI service error";
      try {
        const data = JSON.parse(responseText);
        const parsed =
          typeof data.body === "string" ? JSON.parse(data.body) : data.body;
        errorMessage = parsed?.error || errorMessage;
      } catch {
        errorMessage = responseText || errorMessage;
      }
      this.logError("generateReply failed", {
        status: response.status,
        errorMessage,
      });
      throw new Error(errorMessage);
    }

    // Lambda may return JSON { statusCode, body } or plain text directly
    let replyText: string;
    try {
      const data: AiChatResponse = JSON.parse(responseText);
      if (data.statusCode && data.statusCode !== 200) {
        let errorMessage = "Could not generate reply";
        try {
          const parsed =
            typeof data.body === "string" ? JSON.parse(data.body) : data.body;
          errorMessage = parsed?.error || errorMessage;
        } catch {
          // body may not be JSON for error responses
        }
        this.logError("generateReply non-200", {
          statusCode: data.statusCode,
          errorMessage,
        });
        throw new Error(errorMessage);
      }
      replyText = data.body || responseText;
    } catch (e) {
      // Response is plain text, not JSON — use directly
      if (e instanceof SyntaxError) {
        replyText = responseText;
      } else {
        throw e;
      }
    }

    this.log("generateReply finished", { session_id: request.session_id });
    return { statusCode: 200, body: replyText };
  };
}

export const aiChatService = new AiChatService();
export default AiChatService;
